import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api.service';

interface AdminMaintenancePageProps {
    enabled: boolean | null;
    statusError: string;
    onRefresh: () => Promise<void>;
    onChange: (enabled: boolean) => void;
}

const AdminMaintenancePage: React.FC<AdminMaintenancePageProps> = ({
    enabled,
    statusError,
    onRefresh,
    onChange,
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [shopApiKey, setShopApiKey] = useState('');
    const [isApiKeyConfigured, setIsApiKeyConfigured] = useState(false);
    const [isLoadingApiKeyStatus, setIsLoadingApiKeyStatus] = useState(true);
    const [isSavingApiKey, setIsSavingApiKey] = useState(false);
    const [apiKeyMessage, setApiKeyMessage] = useState('');
    const [apiKeyError, setApiKeyError] = useState('');
    const [shopJobAction, setShopJobAction] = useState<'submit' | 'check' | null>(null);
    const [shopJobMessage, setShopJobMessage] = useState('');
    const [shopJobError, setShopJobError] = useState('');

    useEffect(() => {
        let isMounted = true;
        apiService.get<{ configured: boolean }>('/system-settings/shop-api-key')
            .then((response) => {
                if (isMounted) setIsApiKeyConfigured(response.configured);
            })
            .catch((requestError: any) => {
                if (isMounted) {
                    setApiKeyError(requestError.response?.data?.message || 'Không thể tải trạng thái API key.');
                }
            })
            .finally(() => {
                if (isMounted) setIsLoadingApiKeyStatus(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    const toggleMaintenance = async () => {
        if (enabled === null) return;
        setIsSaving(true);
        setMessage('');
        setError('');
        try {
            const response = await apiService.patch<{ enabled: boolean }>('/system-settings/maintenance', {
                enabled: !enabled,
            });
            onChange(response.enabled);
            setMessage(response.enabled
                ? 'Đã bật chế độ bảo trì. Khách hàng không thể tạo mã chung sức hoặc nạp tiền.'
                : 'Đã tắt chế độ bảo trì. Khách hàng có thể tạo mã chung sức và nạp tiền trở lại.');
        } catch (requestError: any) {
            setError(requestError.response?.data?.message || 'Không thể cập nhật chế độ bảo trì.');
        } finally {
            setIsSaving(false);
        }
    };

    const saveShopApiKey = async () => {
        if (!shopApiKey.trim()) {
            setApiKeyError('Vui lòng nhập API key.');
            setApiKeyMessage('');
            return;
        }

        setIsSavingApiKey(true);
        setApiKeyMessage('');
        setApiKeyError('');
        try {
            const response = await apiService.patch<{ configured: boolean }>('/system-settings/shop-api-key', {
                apiKey: shopApiKey,
            });
            setIsApiKeyConfigured(response.configured);
            setShopApiKey('');
            setApiKeyMessage('Đã lưu API key.');
        } catch (requestError: any) {
            setApiKeyError(requestError.response?.data?.message || 'Không thể lưu API key.');
        } finally {
            setIsSavingApiKey(false);
        }
    };

    const runShopJob = async (action: 'submit' | 'check') => {
        setShopJobAction(action);
        setShopJobMessage('');
        setShopJobError('');
        try {
            if (action === 'submit') {
                const response = await apiService.post<{ data: { submitted: number } }>(
                    '/orders/shop-integration/test-submit-pending',
                );
                setShopJobMessage(`Đã gửi ${response.data.submitted} order pending lên shop.`);
            } else {
                const response = await apiService.post<{
                    data: { completed: number; running: number; invalid: number; notFound: number };
                }>('/orders/shop-integration/test-check-submitted');
                const { completed, running, invalid, notFound } = response.data;
                setShopJobMessage(
                    `Hoàn tất: ${completed}; đang chạy: ${running}; mã lỗi: ${invalid}; chưa thấy trên shop: ${notFound}.`,
                );
            }
        } catch (requestError: any) {
            setShopJobError(requestError.response?.data?.message || 'Không thể chạy kiểm tra shop.');
        } finally {
            setShopJobAction(null);
        }
    };

    return (
        <div className="flex-1 overflow-y-auto bg-gray-50 p-4 md:p-8">
            <div className="mx-auto max-w-3xl space-y-5">
                <div>
                    <p className="text-sm font-medium text-blue-600">Quản trị hệ thống</p>
                    <h2 className="mt-1 text-2xl font-semibold text-gray-900">Chế độ bảo trì</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Khách hàng vẫn xem mã chung sức và sử dụng các chức năng khác, nhưng không thể tạo mã mới hoặc nạp tiền.
                    </p>
                </div>

                {statusError && (
                    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                        <span>{statusError}</span>
                        <button
                            type="button"
                            onClick={() => void onRefresh()}
                            className="rounded-lg border border-red-200 px-3 py-1.5 font-semibold hover:bg-red-100"
                        >
                            Tải lại trạng thái
                        </button>
                    </div>
                )}
                {(message || error) && (
                    <div role={error ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {error || message}
                    </div>
                )}

                <section className="flex flex-wrap items-center justify-between gap-5 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <p className="text-sm text-gray-500">Trạng thái hiện tại</p>
                        <p className={`mt-1 text-xl font-semibold ${enabled ? 'text-amber-700' : enabled === false ? 'text-green-700' : 'text-gray-500'}`}>
                            {enabled === null ? 'Chưa tải được trạng thái' : enabled ? 'Đang bảo trì' : 'Đang hoạt động'}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => void toggleMaintenance()}
                        disabled={enabled === null || isSaving}
                        className={`rounded-xl px-5 py-3 font-semibold text-white transition disabled:cursor-wait disabled:opacity-50 ${enabled ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                    >
                        {isSaving ? 'Đang cập nhật...' : enabled ? 'Tắt bảo trì' : 'Bật bảo trì'}
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">API key gửi order</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {isLoadingApiKeyStatus
                                ? 'Đang kiểm tra trạng thái API key...'
                                : isApiKeyConfigured
                                    ? 'Đã cấu hình API key cho API gửi order.'
                                    : 'Chưa cấu hình API key cho API gửi order.'}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                            API key được lưu bảo mật và không hiển thị lại sau khi lưu.
                        </p>
                    </div>

                    {(apiKeyMessage || apiKeyError) && (
                        <div role={apiKeyError ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${apiKeyError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {apiKeyError || apiKeyMessage}
                        </div>
                    )}

                    <label className="block">
                        <span className="mb-1 block text-sm font-medium text-gray-700">API key (x-api-key)</span>
                        <input
                            type="password"
                            value={shopApiKey}
                            onChange={(event) => setShopApiKey(event.target.value)}
                            maxLength={4000}
                            placeholder="Nhập partner API key"
                            autoComplete="new-password"
                            spellCheck={false}
                            className="w-full rounded-xl border border-gray-300 px-4 py-3 font-mono text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() => void saveShopApiKey()}
                        disabled={isSavingApiKey || isLoadingApiKeyStatus}
                        className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                    >
                        {isSavingApiKey ? 'Đang lưu...' : isApiKeyConfigured ? 'Cập nhật API key' : 'Lưu API key'}
                    </button>
                </section>

                <section className="space-y-4 rounded-2xl bg-white p-5 shadow-sm">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Kiểm tra tích hợp shop</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Chạy thủ công để kiểm tra gửi mã đang chờ hoặc đồng bộ trạng thái các mã đã gửi.
                        </p>
                    </div>

                    {(shopJobMessage || shopJobError) && (
                        <div role={shopJobError ? 'alert' : 'status'} className={`rounded-xl px-4 py-3 text-sm ${shopJobError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                            {shopJobError || shopJobMessage}
                        </div>
                    )}

                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={() => void runShopJob('submit')}
                            disabled={!isApiKeyConfigured || isLoadingApiKeyStatus || shopJobAction !== null}
                            className="rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {shopJobAction === 'submit' ? 'Đang gửi...' : 'Test gửi order pending'}
                        </button>
                        <button
                            type="button"
                            onClick={() => void runShopJob('check')}
                            disabled={!isApiKeyConfigured || isLoadingApiKeyStatus || shopJobAction !== null}
                            className="rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {shopJobAction === 'check' ? 'Đang kiểm tra...' : 'Test kiểm tra trạng thái'}
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default AdminMaintenancePage;
